import {
  getCompanyDetails,
  getCompaniesListService,
  getCompanyListForSelectionService,
  deactivateCompanyService,
  reactivateCompanyService,
  updateCompanySavingsInterestRate,
  editCompanyService,
} from '@/services/companies';
import {
  CompanyForSelectionQueryParams,
  queryKeys,
  CompanyFormFields,
} from '@/types/companies';
import { SelectOption } from '@/types/inputs';
import {
  useMutation,
  useQuery,
  useQueryClient,
  QueryClient,
} from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useAuthUser } from '@/hooks/stores/useAuthUser';
import { createCompanyService } from '@/services/companies';
import useIdempotencyKeyGenerator from '../useIdempotencyKeyGenerator';

function clearQueryCache(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: [queryKeys.list],
  });
  queryClient.refetchQueries({
    queryKey: [queryKeys.details],
  });
}

export const useGetCompanyList = () => {
  const [searchParams] = useSearchParams();
  const params = searchParams.toString();

  const { data, isPending, isSuccess } = useQuery({
    queryKey: [queryKeys.list, params],
    queryFn: () => getCompaniesListService(params),
  });

  return {
    isSuccess,
    companies: data?.companies.records || [],
    aggregateInfo: data?.aggregateInfo,
    isCompaniesPending: isPending,
    totalRecordCount: data?.companies.totalRecordCount || 0,
  };
};

export const useGetCompanyDetails = (uuid: string) => {
  const { data, isPending, isError, error } = useQuery({
    queryFn: () => getCompanyDetails(uuid),
    queryKey: [queryKeys.details, uuid],
    enabled: !!uuid,
  });

  return {
    companyDetails: data,
    isCompanyDetailsLoading: isPending,
    isError,
    error,
  };
};

export const useGetCompanyListForSelection = (
  queryParams: CompanyForSelectionQueryParams,
  enabled = true,
) => {
  const params = new URLSearchParams(queryParams).toString();
  const { isStaff } = useAuthUser();
  const { data: companiesList = [], isPending: isCompaniesListLoading } =
    useQuery({
      queryFn: () => getCompanyListForSelectionService(params),
      queryKey: [queryKeys.list, queryKeys.selectionList, params],
      select: data => {
        const mappedItems: SelectOption[] = data
          ? data.map(company => {
              return {
                ...company,
                value: company.uuid,
                label: company.name,
              };
            })
          : [];

        return mappedItems;
      },
      enabled: enabled && isStaff,
    });

  return {
    companiesList,
    isCompaniesListLoading,
  };
};

export const useDeactivateCompany = (uuid: string, enabled = false) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (teammateId?: string) =>
      deactivateCompanyService(uuid, false, teammateId),
    onSuccess: () => clearQueryCache(queryClient),
  });
  const { data, isFetching: isCanDeactivateCompanyPending } = useQuery({
    queryKey: [queryKeys.preflight, uuid],
    queryFn: () => deactivateCompanyService(uuid, true),
    enabled,
  });

  return {
    canDeactivateCompany: data?.mustReplaceTeammate === false || false,
    isCanDeactivateCompanyPending,
    deactivateCompany: mutateAsync,
    isDeactivateCompanyPending: isPending,
  };
};

export const useReactivateCompany = (teammateId: string) => {
  const queryClient = useQueryClient();

  const {
    mutateAsync: reactivateCompany,
    isPending: isReactivateCompanyPending,
  } = useMutation({
    mutationFn: () => reactivateCompanyService(teammateId),
    onSuccess: () => {
      clearQueryCache(queryClient);
    },
  });

  return {
    reactivateCompany,
    isReactivateCompanyPending,
  };
};

export const useUpdateCompanySavingsInterestRate = (uuid: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (rateSavingInterest: number) =>
      updateCompanySavingsInterestRate(uuid, rateSavingInterest),
    onSuccess: () => {
      clearQueryCache(queryClient);
    },
  });
};

export const useAddCompany = () => {
  const idempotencyKey = useIdempotencyKeyGenerator();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (companyData: CompanyFormFields) =>
      createCompanyService({ ...companyData, idempotencyKey }),
    onSuccess: () => {
      clearQueryCache(queryClient);
    },
  });

  return {
    addCompany: mutateAsync,
    isAddCompanyPending: isPending,
  };
};

export const useEditCompany = () => {
  const idempotencyKey = useIdempotencyKeyGenerator();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (companyData: CompanyFormFields) =>
      editCompanyService({ ...companyData, idempotencyKey }),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    editCompany: mutateAsync,
    isEditCompanyPending: isPending,
  };
};
